import { Link } from "react-router-dom";
import { ArrowLeft, Send, Users, Globe, Clock, DollarSign, BarChart3, Target, ClipboardList, Search, Share2, Image, Star, Monitor, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "../components/Footer";

const API = process.env.REACT_APP_BACKEND_URL;

const taskTypes = [
  { icon: ClipboardList, title: "Data Entry", desc: "Entering data into spreadsheets or databases" },
  { icon: Search, title: "Online Research", desc: "Gathering information on specific topics" },
  { icon: BarChart3, title: "Surveys & Polls", desc: "Participating in polls and surveys" },
  { icon: Star, title: "Product Testing", desc: "Testing products and writing reviews" },
  { icon: Share2, title: "Social Media Tasks", desc: "Liking, sharing, and commenting on posts" },
  { icon: Image, title: "Image & Video Tagging", desc: "Tagging and categorizing media" },
  { icon: Star, title: "App Reviews", desc: "Downloading and reviewing mobile apps" },
  { icon: Monitor, title: "Website Testing", desc: "Providing website usability feedback" },
];

const benefits = [
  { icon: Users, title: "Global Workforce", desc: "Access to thousands of workers worldwide, ready to complete your tasks 24/7" },
  { icon: Clock, title: "Fast Turnaround", desc: "Get work done quickly with our on-demand workforce available around the clock" },
  { icon: DollarSign, title: "Cost Effective", desc: "Pay only for completed work with transparent pricing and no hidden fees" },
  { icon: Target, title: "Targeted Reach", desc: "Filter workers by location, demographics, and skills for your specific needs" },
];

export default function AdvertisePage() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    website: "",
    country: "",
    address: "",
    description: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Submit as contact form with advertiser context
      const response = await axios.post(`${API}/v2/contact`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        subject: "Advertiser Inquiry",
        message: `
Phone: ${formData.phone || "Not provided"}
Website: ${formData.website || "Not provided"}
Country: ${formData.country || "Not provided"}
Address: ${formData.address || "Not provided"}

Description:
${formData.description}
        `.trim(),
        type: "advertiser"
      });
      toast.success("Thank you for your interest! Our team will contact you within 24-48 hours.");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        website: "",
        country: "",
        address: "",
        description: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <img src="/images/gigseasy-logo.svg" alt="GigsEasy" className="h-12 mx-auto mb-8 brightness-0 invert" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Access A Global On-Demand<br />Workforce 24/7
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            GigsEasy connects you with thousands of workers ready to complete your tasks. 
            From data entry to app reviews, get work done quickly and cost-effectively.
          </p>
          <a href="#contact-form">
            <Button size="lg" className="bg-[#00B894] hover:bg-[#00a383] text-white px-8 py-6 text-lg rounded-full">
              Get Started Today
            </Button>
          </a>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Sign Up With GigsEasy</h3>
              <p className="text-slate-600">Fill out the registration form with your details and submit your request.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Verify Your Account</h3>
              <p className="text-slate-600">Our team will review and manually approve your account within 24-48 hours.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Post Your Tasks</h3>
              <p className="text-slate-600">Fill out the task details, set your budget, and publish your tasks to our workforce.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">Why Choose GigsEasy?</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            There are still many things that humans can do much more effectively than computers.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="p-6 border border-slate-200 rounded-2xl hover:border-[#00B894] transition-colors">
                <benefit.icon className="w-10 h-10 text-[#00B894] mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Task Types */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">Types of Tasks You Can Create</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Outsource microtasks to our global workforce and free up your internal staff for higher-value activities.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {taskTypes.map((task, index) => (
              <div key={index} className="bg-white p-5 rounded-xl border border-slate-200 hover:border-[#00B894] hover:shadow-md transition-all">
                <task.icon className="w-8 h-8 text-[#00B894] mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1">{task.title}</h3>
                <p className="text-sm text-slate-500">{task.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Detailed Reporting & Transparency</h2>
              <p className="text-lg text-slate-600 mb-8">
                You will have complete visibility and control over the tasks assigned to the GigsEasy community, 
                promoting accountability and transparency in your projects.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#00B894]" />
                  <span className="text-slate-700 font-medium">Real-Time Task Tracking</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#00B894]" />
                  <span className="text-slate-700 font-medium">Detailed Completion Reports</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#00B894]" />
                  <span className="text-slate-700 font-medium">Multi-Filter Worker Targeting</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#00B894]" />
                  <span className="text-slate-700 font-medium">Dedicated Customer Support</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-100 to-slate-50 rounded-3xl p-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-slate-900">Task Analytics</h4>
                  <span className="text-xs text-slate-500">Live</span>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Tasks Completed</span>
                    <span className="font-bold text-[#00B894]">1,247</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-[#00B894] h-2 rounded-full" style={{width: '78%'}}></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Success Rate</span>
                    <span className="font-bold text-[#00B894]">98.5%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Avg. Completion Time</span>
                    <span className="font-bold text-slate-700">2.3 mins</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Get Started Today</h2>
            <p className="text-lg text-slate-600">
              Fill out the form below and our team will contact you within 24-48 hours to discuss your needs.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-slate-700">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="John"
                    className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                    required
                    data-testid="advertise-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-slate-700">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Doe"
                    className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                    required
                    data-testid="advertise-lastname"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                    required
                    data-testid="advertise-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                    data-testid="advertise-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website" className="text-slate-700">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://yourcompany.com"
                    className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                    data-testid="advertise-website"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country" className="text-slate-700">Country *</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="United States"
                    className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                    required
                    data-testid="advertise-country"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-slate-700">Business Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Business St, Suite 100, City, State ZIP"
                  className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                  data-testid="advertise-address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-slate-700">What are you looking for? *</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Please describe the type of tasks you want to create, your expected volume, target audience, and any specific requirements..."
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-slate-200 focus:border-[#00B894] focus:outline-none rounded-xl resize-none"
                  required
                  data-testid="advertise-description"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-[#00B894] to-[#0984E3] text-white font-medium rounded-full shadow-lg shadow-[#00B894]/25 hover:opacity-90 transition-all"
                data-testid="advertise-submit"
              >
                {loading ? "Submitting..." : "Submit Request"}
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
